import React, {lazy, Suspense, useCallback, useContext, useEffect} from 'react'

import './App.css'
import {AuthContext, PageContext} from "@lb/mlib-components";
import {Navigate, Outlet, Route, Routes, useLocation} from "react-router";
import CenteredBox from "./components/ui/CenteredBox";
import Button from "@mui/material/Button";
import Reconcile from "./components/production/Reconcile";
import HolidaysPage from "./pages/HolidaysPage";
import Samples from "./components/production/Samples/Samples";
import {trackScreen} from "./utils/reactGA";
import Logistics from "./components/production/Logistics";


const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProductionPage = lazy(() => import('./pages/ProductionPage'));
const QualityPage = lazy(() => import('./pages/QualityPage'));
const ForPickupPage = lazy(() => import('./pages/shipping/ForPickupPage'));
const HistoryPage = lazy(() => import('./pages/shipping/HistoryPage'));
const SalesOrder = lazy(() => import('./components/audit/SalesOrder'));
const AlloyTariffsSalesOrder = lazy(() => import('./components/audit/alloytariffs/SalesOrder'));
const Shipment = lazy(() => import('./components/audit/Shipment/Shipment'));
const TransitPage = lazy(() => import('./pages/shipping/TransitPage'));
const CouriersPage = lazy(() => import('./pages/CouriersPage'));
const TargetsPage = lazy(() => import('./pages/TargetsPage'));
const FreeItemsPage = lazy(() => import('./pages/FreeItemsPage'));
const EditFreeItem = lazy(() => import('./components/freeitems/EditFreeItem'));
const PriceCutsPage = lazy(() => import('./pages/PriceCutsPage'));
const ProductionOrder = lazy(() => import('./components/production/ProductionOrder'));
const ProductionOrderByShape = lazy(() => import('./components/production/ProductionOrderByShape'));
const ProductionOrderByFinish = lazy(() => import('./components/production/ProductionOrderByFinish'));
const StockItem = lazy(() => import('./components/audit/StockItem'));
const AuditStockPage = lazy(() => import('./pages/audit/AuditStockPage'));
const AuditOrdersPage = lazy(() => import('./pages/audit/AuditOrdersPage'));
const QCIssuedPage = lazy(() => import('./pages/QCIssuedPage'));
const Issue = lazy(() => import('./components/production/QC/Issue'));
const AuditClonedPage = lazy(() => import('./pages/audit/AuditClonedPage'));
const TestOrdersPage = lazy(() => import('./pages/TestOrdersPage'));
const AuditWarehousePage = lazy(() => import('./pages/audit/AuditWarehousePage'));
const AuditShipmentPage = lazy(() => import('./pages/audit/AuditShipmentPage'));
const SchedulerPage = lazy(() => import('./pages/shipping/SchedulerPage'));
const OrdersPage = lazy(() => import('./pages/shipping/PreparePage'));

const DimsPage = lazy(() => import("./pages/DimsPage"));
const TrackersPage = lazy(() => import("./pages/TrackersPage"));
const Stations = lazy(() => import("./components/stations/Stations"));
const Station = lazy(() => import("./components/stations/Station"));
const Printers = lazy(() => import("./components/printers/Printers"));
const Printer = lazy(() => import("./components/printers/Printer"));
const AlloyTariffsPage = lazy(() => import("./pages/AlloyTariffsPage"));
const FileUploadPage = lazy(() => import("./pages/FileUploadPage"));
const Home = lazy(() => import("./pages/home/home"));
const Forbidden = lazy(() => import("./pages/403"));
const NotFound = lazy(() => import("./pages/404"));
const FulfillmentPage = lazy(() => import("./pages/shipping/FulfilmentPage"));

const Finance = lazy(() => import( "./components/production/Finance"));
const AuditLBShipPage = lazy(() => import( "./pages/audit/AuditLBShipPage"));
const GlobalSearchPage = lazy(() => import( "./pages/GlobalSearchPage"));

// Module scope (not a useCallback inside RouterRoutes) so the component identity stays
// stable across navigations — a changing identity would remount every protected page and
// throw away its state. It reads the location itself to record where the user was headed.
function ProtectedRoute({isAllowed, redirectPath}) {
    const location = useLocation()

    if (isAllowed) return <Outlet/>
    else {
        // `state.from` is what SignIn redirects back to once the user is authenticated;
        // the redirect below is a `replace`, so the attempted entry is gone from history.
        if (redirectPath) return <Navigate to={redirectPath} replace
                                           state={{from: location.pathname + location.search}}/>
        else return <Forbidden/>
    }
}

export default function RouterRoutes() {
    const authCtx = useContext(AuthContext)
    const pageCtx = useContext(PageContext)
    const location = useLocation()

    const {build} = window['runConfig'];

    // track location(url-pathname) change to kick out user without api call
    const isLoggedIn = useCallback(() => location && authCtx.isLoggedIn(), [authCtx, location])

    useEffect(() => {
        trackScreen(pageCtx.title + ' - ' + location.pathname);
    }, [location.pathname, pageCtx.title]);

    return (
        <Suspense fallback={<CenteredBox><Button loading> </Button></CenteredBox>}>
            <Routes>

                <Route path='/'>
                    <Route path='/' element={<Home/>}/>
                </Route>

                {/* `Shared` paths */}
                <Route path='dashboard'>
                    <Route index element={<Navigate to={'today'} replace/>}/>
                    <Route path='today' element={<DashboardPage title="Today"/>}></Route>
                    <Route path='future' element={<DashboardPage title="Future"/>}></Route>
                    <Route path='intransit' element={<DashboardPage title="In Transit"/>}></Route>
                    <Route path='map' element={<DashboardPage title="Map"/>}></Route>
                </Route>

                {/* Google login now lives inline on the landing page (`/`), so `/login` just bounces there. */}
                <Route path='login' element={<Navigate to='/' replace/>}/>

                {/* `Logged in` routes*/}
                <Route element={<ProtectedRoute isAllowed={isLoggedIn()} redirectPath='login'/>}>

                    <Route path='globalsearch' element={<GlobalSearchPage/>}/>

                    <Route path='shipping'>
                        <Route path='prepare'>
                            <Route index element={<OrdersPage/>}/>
                            <Route path=':salesorderid' element={<OrdersPage/>}/>
                        </Route>
                        <Route path='fulfilment'>
                            <Route index element={<FulfillmentPage/>}/>
                            <Route path=':shipmentid' element={<FulfillmentPage/>}/>
                        </Route>
                        <Route path='scheduler'>
                            <Route index element={<SchedulerPage/>}/>
                            <Route path=':shipmentid' element={<SchedulerPage/>}/>
                            <Route path='delete/*' element={<SchedulerPage delete={true}/>}/>
                        </Route>
                        <Route path='forpickup'>
                            <Route index element={<ForPickupPage/>}/>
                            <Route path=':shipmentid' element={<ForPickupPage/>}/>
                            <Route path='delete/*' element={<ForPickupPage delete={true}/>}/>
                        </Route>
                        <Route path='transit'>
                            <Route index element={<TransitPage/>}/>
                            <Route path=':shipmentid' element={<TransitPage/>}/>
                        </Route>
                        <Route path='history'>
                            <Route index element={<HistoryPage/>}/>
                            <Route path=':shipmentid' element={<HistoryPage/>}/>
                        </Route>
                    </Route>

                    <Route path='production'>
                        <Route index element={<Navigate to={'orders'} replace/>}/>
                        <Route path='stock'>
                            <Route index element={<AuditStockPage tab={"stock"}/>}/>
                            <Route path=':sku' element={<StockItem/>}/>
                        </Route>
                        <Route path='lots'>
                            <Route index element={<AuditStockPage tab={"lots"}/>}/>
                            <Route path=':lotid' element={<AuditStockPage tab={"lots"}/>}/>
                        </Route>
                        <Route path='reconcile' element={<Reconcile/>}/>
                        <Route path='shapes'>
                            <Route index element={<ProductionPage tab={"shapes"}/>}/>
                            <Route path=':shape' element={<ProductionOrderByShape/>}/>
                        </Route>
                        <Route path='finishes'>
                            <Route index element={<ProductionPage tab={"finishes"}/>}/>
                            <Route path=':finish' element={<ProductionOrderByFinish/>}/>
                        </Route>
                        <Route path='orders'>
                            <Route index element={<ProductionPage tab={"orders"}/>}/>
                            <Route path=':productionorderid' element={<ProductionOrder/>}/>
                        </Route>
                        <Route path='quality'>
                            <Route index element={<QualityPage/>}/>
                            <Route path=':quality' element={<QualityPage/>}/>
                        </Route>
                        <Route path='qchold'>
                            <Route index element={<QCIssuedPage/>}/>
                            <Route path=':issueid' element={<Issue/>}/>
                        </Route>
                    </Route>

                    <Route path='reporting'>
                        <Route path='finance'>
                            <Route index element={<Finance/>}/>
                        </Route>
                        <Route path='sampleorders'>
                            <Route index element={<Samples/>}/>
                        </Route>
                        <Route path='logistics'>
                            <Route index element={<Logistics/>}/>
                        </Route>
                    </Route>

                    {build === "Development" &&
                        <Route path='testorders'>
                            <Route index element={<TestOrdersPage/>}/>
                        </Route>}

                    <Route path='audit'>
                        {build === "Development" &&
                            <Route path='clone'>
                                <Route index element={<AuditClonedPage/>}/>
                            </Route>}
                        <Route path='salesorders'>
                            <Route index element={<AuditOrdersPage/>}/>
                            <Route path=':salesorderid' element={<SalesOrder/>}/>
                        </Route>
                        <Route path='warehouse' element={<AuditWarehousePage/>}/>
                        <Route path='shipments'>
                            <Route index element={<AuditShipmentPage/>}/>
                            <Route path=':shipmentid' element={<Shipment/>}/>
                        </Route>
                        <Route path='lbship'>
                            <Route path='expected'>
                                <Route index element={<AuditLBShipPage tab={"expected"}/>}/>
                            </Route>
                            <Route path='allocations'>
                                <Route index element={<AuditLBShipPage tab={"allocations"}/>}/>
                            </Route>
                            <Route path='orders'>
                                <Route index element={<AuditLBShipPage tab={"orders"}/>}/>
                            </Route>
                            <Route path='at_risk'>
                                <Route index element={<AuditLBShipPage tab={"at_risk"}/>}/>
                            </Route>
                            <Route path='ext_uses'>
                                <Route index element={<AuditLBShipPage tab={"inv_violations"}/>}/>
                            </Route>
                            <Route path='snapshots'>
                                <Route index element={<AuditLBShipPage tab={"snapshots"}/>}/>
                            </Route>
                        </Route>
                        <Route element={<ProtectedRoute
                            isAllowed={authCtx.hasRole('audit_tariffs')}/>}>
                            <Route path='alloytariffs'>
                                <Route index element={<AlloyTariffsPage/>}/>
                                <Route path=':shipmentid' element={<AlloyTariffsSalesOrder/>}/>

                            </Route>
                            <Route path='fileupload'>
                                <Route path="/audit/fileupload/:purchaseId?" element={<FileUploadPage/>}/>
                            </Route>
                        </Route>
                    </Route>


                    <Route path='settings'>
                        <Route path='freeitems'>
                            <Route index element={<FreeItemsPage/>}/>
                            <Route path=':code' element={<EditFreeItem/>}/>
                        </Route>
                        <Route path='pricecuts'>
                            <Route index element={<PriceCutsPage/>}/>
                        </Route>
                        <Route path='targets' element={<TargetsPage/>}/>
                        <Route element={<ProtectedRoute isAllowed={authCtx.hasRole('shipping')}/>}>
                            <Route path='couriers' element={<CouriersPage/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={authCtx.hasRole('fulfilment')}/>}>
                            <Route path='dims' element={<DimsPage/>}/>
                            <Route path='trackers' element={<TrackersPage/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={authCtx.hasRole('admin')}/>}>
                            <Route path='stations'>
                                <Route index element={<Stations/>}/>
                                <Route path=':code' element={<Station/>}/>
                            </Route>
                            <Route path='printers'>
                                <Route index element={<Printers/>}/>
                            </Route>
                            <Route path='holidays'>
                                <Route index element={<HolidaysPage/>}/>
                            </Route>
                        </Route>
                    </Route>

                </Route>

                <Route path='*' element={authCtx.isLoggedIn() ? <NotFound/> : <Navigate to={'/'} replace/>}/>

            </Routes>
        </Suspense>
    )
}